/**
 * @voltxampere/schema — Migration runner
 *
 * Per SPECS §1.8. Given an unknown blob that claims a `schemaVersion`,
 * walk through the registered migrations until the current schema
 * version is reached, then validate through `CircuitSchema.parse` (which
 * throws `ZodError` on failure — that's the terminal validator
 * exception to our otherwise `.safeParse()`-everywhere rule, documented
 * in ADR-0004).
 *
 * The chain search is a straight forward-pointer walk, not a general
 * graph search: at any version, at most one migration whose `from`
 * equals that version is picked (ambiguous registries are a contract
 * failure and throw `MigrationError`). Identity migrations
 * (`from === to`) are rejected as degenerate to prevent infinite loops.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { CircuitSchema } from "../schemas/circuit.js";
import { MIGRATIONS } from "./registry.js";
import { MigrationError } from "./types.js";
/**
 * The current schema version this package produces. Kept in sync with
 * `PACKAGE_VERSION` at package boundary; bumped whenever a new
 * migration is registered.
 */
export const CURRENT_SCHEMA_VERSION = "0.4.0";
/**
 * Walk `migrations` from `from` to `to`. Deterministic — each version
 * is reached by exactly one migration — and bounded — the chain may
 * not exceed the number of migrations in the registry (detects cycles
 * without needing a visited-set heuristic).
 */
export function findMigrationPath(from, to, migrations = MIGRATIONS) {
    if (from === to)
        return [];
    const path = [];
    let current = from;
    const maxSteps = migrations.length;
    for (let step = 0; step < maxSteps; step += 1) {
        const candidates = migrations.filter((m) => m.from === current);
        if (candidates.length === 0) {
            throw new MigrationError(`No migration registered from "${current}" towards "${to}".`, current, to);
        }
        if (candidates.length > 1) {
            throw new MigrationError(`Ambiguous migration registry: multiple migrations start at "${current}".`, current, to);
        }
        const next = candidates[0];
        /* v8 ignore start — unreachable: the length === 0 branch above returns first */
        if (next === undefined) {
            throw new MigrationError(`No migration registered from "${current}" towards "${to}".`, current, to);
        }
        /* v8 ignore stop */
        if (next.to === next.from) {
            throw new MigrationError(`Degenerate migration ${next.from} → ${next.to} would loop.`, current, to);
        }
        path.push(next);
        current = next.to;
        if (current === to)
            return path;
    }
    throw new MigrationError(`Migration chain from "${from}" did not reach "${to}" within ${maxSteps.toString()} steps.`, from, to);
}
/**
 * Internal runner shared by `migrateToLatest` and the test-only
 * `migrateWithMigrations` below. Applies each migration in order,
 * running any `validate` post-condition.
 */
function runMigrations(data, migrations) {
    let current = data;
    for (const step of migrations) {
        try {
            current = step.migrate(current);
        }
        catch (cause) {
            throw new MigrationError(`Migration ${step.from} → ${step.to} threw during migrate().`, step.from, step.to, cause);
        }
        if (step.validate && !step.validate(current)) {
            throw new MigrationError(`Migration ${step.from} → ${step.to} produced invalid output.`, step.from, step.to);
        }
    }
    return current;
}
/**
 * Per SPECS §1.8. Takes a raw unknown value that claims a
 * `schemaVersion`, runs it forward to `CURRENT_SCHEMA_VERSION`, and
 * returns a fully validated `Circuit`.
 *
 * Missing `schemaVersion` is treated as `"0.0.0"` per SPECS §1.8
 * comment: the old "pre-versioning" world.
 */
export function migrateToLatest(raw) {
    return migrateWithMigrations(raw, MIGRATIONS, CURRENT_SCHEMA_VERSION);
}
/**
 * Same as `migrateToLatest` but accepts an explicit migration list and
 * target version. Intended for tests; callers should almost always use
 * `migrateToLatest`.
 */
export function migrateWithMigrations(raw, migrations, targetVersion) {
    const currentVersion = typeof raw === "object" && raw !== null && "schemaVersion" in raw
        ? typeof raw.schemaVersion === "string"
            ? raw.schemaVersion
            : "0.0.0"
        : "0.0.0";
    const path = findMigrationPath(currentVersion, targetVersion, migrations);
    const migrated = runMigrations(raw, path);
    return CircuitSchema.parse(migrated);
}
//# sourceMappingURL=migrate.js.map